<template>
  <div class="payment-element" data-cy="payment-element">
    <div
      v-if="error && isExpressConfirm"
      data-cy="payment-element-errors"
      class="payment-element__errors"
      role="alert"
    >
      <v-icon icon="error" />
      <span>{{ error }}</span>
    </div>

    <template v-if="!hideExpressButtons">
      <div
        :class="{
          'pb-3':
            expressHasPaymentMethods ||
            isLoadingElement(STRIPE_ELEMENTS.EXPRESS_CHECKOUT)
        }"
      >
        <div
          v-show="isInitialized(STRIPE_ELEMENTS.EXPRESS_CHECKOUT)"
          :ref="STRIPE_ELEMENTS.EXPRESS_CHECKOUT"
        ></div>

        <placeholder-rect
          v-if="!isInitialized(STRIPE_ELEMENTS.EXPRESS_CHECKOUT)"
          class="payment-element__express-placeholder"
          full-width
          :height="`${BUTTON_HEIGHT}px`"
        />
      </div>
    </template>

    <div :class="{ 'pb-3': !isAuthenticated }" ref="paymentWrapper">
      <div
        :class="[
          {
            'payment-element__wrapper': isPaymentInitialized,
            '--guest': !isAuthenticated,
            '--with-express': expressHasPaymentMethods && !hideExpressButtons
          }
        ]"
      >
        <template>
          <div v-if="showAddressElement" class="payment-element__address mb-2">
            <div :ref="STRIPE_ELEMENTS.ADDRESS" id="address-element"></div>
          </div>

          <div v-if="!isAuthenticated" class="mb-2">
            <div :ref="STRIPE_ELEMENTS.LINK_AUTHENTICATION"></div>

            <email-field-cta
              v-if="isInitialized(STRIPE_ELEMENTS.LINK_AUTHENTICATION)"
              :class="{ 'mb-3 mt-2': emailExists }"
              :email-exists="emailExists"
            />
          </div>
          <div :ref="STRIPE_ELEMENTS.PAYMENT" id="payment-element"></div>
        </template>

        <template v-if="isPaymentInitialized">
          <transition name="fade" mode="out-in">
            <div ref="payButton" class="d-flex align-items-center pt-3">
              <v-button
                data-cy="payment-element-submit"
                primary
                block
                :disabled="disabled"
                :is-loading="isLoading || isLoadingInfo"
                @click="handleSubmit"
              >
                {{ buttonText }}
              </v-button>
            </div>
          </transition>
        </template>
      </div>

      <div class="payment-element__components">
        <div
          v-if="error && !isExpressConfirm"
          data-cy="payment-element-errors"
          class="payment-element__errors"
          role="alert"
        >
          <v-icon icon="error" />
          <span>{{ error }}</span>
        </div>

        <transition name="fade" mode="out-in">
          <template v-if="!isPaymentInitialized">
            <div class="payment-element__placeholder payment-element__wrapper">
              <div class="d-flex w-100">
                <div class="payment-element__placeholder-element"></div>
                <div class="payment-element__placeholder-element"></div>
              </div>
              <div class="d-flex w-100 my-3">
                <div class="payment-element__placeholder-element"></div>
              </div>
              <div class="d-flex w-100 mt-3 mb-2">
                <div class="payment-element__placeholder-element"></div>
                <div class="payment-element__placeholder-element"></div>
              </div>
            </div>
          </template>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import { email } from "vuelidate/lib/validators"

import { isPresent } from "@/lib/utils"
import useStripe from "@/lib/mixins/use-stripe"
import {
  generateReturnPath,
  INTENT_TYPES,
  CALLBACK_PARAMS
} from "@/lib/checkout-utils"
import debounce from "@/lib/debounce"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { getReferralData } from "@/lib/rewardful"

import { STAGES as AUTH_STAGES } from "@/components/AuthModal"

import { ACTIVITY_TYPES } from "@/store/analytics"

import ERROR_REASONS from "@shared/payments/error-reasons.json"

export const STRIPE_ELEMENTS = {
  PAYMENT: "payment",
  ADDRESS: "address",
  EXPRESS_CHECKOUT: "expressCheckout",
  LINK_AUTHENTICATION: "linkAuthentication" // email
}

const BUTTON_HEIGHT = 52

export default {
  mixins: [useStripe("setupForm")],

  components: {
    PlaceholderRect: () => import("@/components/PlaceholderRect"),
    EmailFieldCta: () => import("@/components/checkout/EmailFieldCta")
  },

  props: {
    hideExpressButtons: { type: Boolean, default: false },
    intentWillBeGiven: { type: Boolean, default: false },
    intentType: { type: String, default: null }, // null triggers loading state
    intentClientSecret: { type: String, default: null }, // null triggers loading state
    productId: { type: [String, Number], default: null },
    productVersionId: { type: [String, Number], default: null },
    isSubscription: { type: Boolean, default: false },
    amount: { type: [String, Number], default: null },
    amountCents: { type: Number, default: null },
    currency: { type: String, default: null },
    symbol: { type: String, default: "$" },
    couponCode: { type: String, default: null },
    hasTrial: { type: Boolean, default: false },
    confirmText: { type: String, default: null },
    isLoadingInfo: { type: Boolean, default: false },
    email: { type: String, default: "" },
    emailExists: { type: Boolean, default: false },
    orderBumps: { type: Array, default: () => [] }
  },

  model: {
    prop: "email",
    event: "input"
  },

  data() {
    return {
      I_intentType: this.intentType, // I_ for internal
      I_intentClientSecret: this.intentClientSecret, // I_ for internal

      setupAttempt: 0,

      error: null,
      isExpressConfirm: false,
      isGuestCheckout: false,
      isValid: false,
      isAddressValid: false,
      isLoading: false,
      userDetails: null, // name + address
      elements: null,
      authToken: null,

      expressHasPaymentMethods: true,

      initializedElements: {},
      loadingElements: {},

      STRIPE_ELEMENTS,
      BUTTON_HEIGHT
    }
  },

  computed: {
    ...mapGetters(["hasEnabledTaxCollection"]),
    ...mapGetters("localisation", ["currentLocale"]),
    ...mapGetters("auth", ["isAuthenticated", "currentUser", "isGuest"]),

    buttonText() {
      return isPresent(this.confirmText)
        ? this.confirmText
        : this.hasTrial && this.amountCents === 0
        ? this.$t("components.checkout.paymentElement.startFree")
        : isPresent(this.amount) && isPresent(this.currency)
        ? `${this.$t("components.checkout.paymentElement.pay")} ${this.symbol}${
            this.amount
          } ${this.currency?.toUpperCase()}`
        : this.$t("components.checkout.paymentElement.pay")
    },

    isIntentGiven() {
      return this.I_intentClientSecret && this.I_intentType
    },

    showAddressElement() {
      return this.hasEnabledTaxCollection && !this.isIntentGiven
    },

    isInvalidEmail() {
      return (
        (this.isGuest && !this.isEmailValid(this.email)) || this.emailExists
      )
    },

    disabled() {
      return (
        !this.isValid ||
        this.isInvalidEmail ||
        (this.showAddressElement ? !this.isAddressValid : false) ||
        this.isLoadingInfo
      )
    },

    isPaymentInitialized() {
      return (
        this.isInitialized(STRIPE_ELEMENTS.PAYMENT) &&
        (this.showAddressElement
          ? this.isInitialized(STRIPE_ELEMENTS.ADDRESS)
          : true)
      )
    },

    elementsMode() {
      return this.amountCents === 0
        ? "setup"
        : this.isSubscription
        ? "subscription"
        : "payment"
    }
  },

  watch: {
    amountCents() {
      if (!this.elements) return

      this.elements.update({
        amount: this.amountCents,
        mode: this.elementsMode
      })
    },

    isIntentGiven() {
      if (this.isIntentGiven && this.intentWillBeGiven) {
        this.setupForm()
      }
    },

    intentType() {
      if (this.intentWillBeGiven) {
        this.I_intentType = this.intentType
      }
    },

    intentClientSecret() {
      if (this.intentWillBeGiven) {
        this.I_intentClientSecret = this.intentClientSecret
      }
    },

    elementsMode() {
      if (this.elements) {
        this.elements.update({ mode: this.elementsMode })
      }
    }
  },

  mounted() {
    this.setupForm()
  },

  methods: {
    ...mapActions("analytics", ["trackActivity"]),
    ...mapActions("products", ["fetchIntent"]),
    ...mapActions("auth", ["fetchCurrentProvider", "verifySubscriber"]),

    isInitialized(element) {
      return this.initializedElements[element]
    },

    isEmailValid(mail) {
      return mail && email(mail)
    },

    isLoadingElement(element) {
      return this.loadingElements[element]
    },

    setLoadingElement(element, value) {
      this.$set(this.loadingElements, element, value)
    },

    setInitialized(element, value) {
      this.$set(this.initializedElements, element, value)
    },

    setEmail(email) {
      this.$emit("input", email)
    },

    borderRadius() {
      if (!window.getComputedStyle) {
        return
      }

      const style = getComputedStyle(document.body)
      return style.getPropertyValue("--input-border-radius")
    },

    mountElement(stripeElement, callback, retries = 1) {
      if (
        !this.isInitialized(stripeElement) &&
        !this.isLoadingElement(stripeElement)
      ) {
        if (!this.$refs[stripeElement]) {
          if (retries < 20) {
            setTimeout(
              () => this.mountElement(stripeElement, callback, retries + 1),
              500
            )
          } else {
            console.error("Element not found", stripeElement)
          }

          return
        }

        this.setLoadingElement(stripeElement, true)

        const element = callback()

        element.on("loaderror", err => {
          console.error(err)

          this.setLoadingElement(stripeElement, false)
        })

        element.on("ready", () => {
          this.setLoadingElement(stripeElement, false)
          this.setInitialized(stripeElement, true)
        })

        element.mount(this.$refs[stripeElement])
      }
    },

    initAuthElement() {
      this.mountElement(STRIPE_ELEMENTS.LINK_AUTHENTICATION, () => {
        const options = this.email
          ? {
              defaultValues: {
                email: this.email
              }
            }
          : {}

        const authElement = this.elements.create("linkAuthentication", options)

        authElement.on("change", event => {
          this.setEmail(event.value.email)

          debounce(this.handleUserDetailsChange, 600)
        })

        return authElement
      })
    },

    initPaymentElement() {
      this.mountElement(STRIPE_ELEMENTS.PAYMENT, () => {
        const paymentElement = this.elements.create("payment", {
          defaultValues: {
            billingDetails: {
              name: this.currentUser?.name || "",
              email: this.currentUser?.email || this.email || ""
            }
          },
          paymentMethodOrder: [
            "card",
            "apple_pay",
            "google_pay",
            "link",
            "paypal",
            "afterpay_clearpay",
            "zip",
            "klarna",
            "ideal",
            "blik",
            "sepa_debit",
            "swish",
            "au_becs_debit",
            "bancontact",
            "cashapp",
            "giropay",
            "paynow",
            "sofort"
          ]
        })

        paymentElement.on("change", this.handlePaymentElementChange)

        paymentElement.on("loaderstart", () => {
          this.setInitialized(STRIPE_ELEMENTS.PAYMENT, true)
        })

        return paymentElement
      })
    },

    initAddressElement() {
      this.mountElement(STRIPE_ELEMENTS.ADDRESS, () => {
        const addressElement = this.elements.create("address", {
          mode: "billing",
          defaultValues: {
            name: this.currentUser.name || ""
          }
        })

        addressElement.on("change", this.handleAddressElementChange)

        addressElement.on("loaderstart", () => {
          this.setInitialized(STRIPE_ELEMENTS.ADDRESS, true)
        })

        return addressElement
      })
    },

    showAuthModal() {
      this.$router.push(AUTH_STAGES.INITIAL, { append: true })
    },

    hideExpressWindow() {
      this.elements.getElement(STRIPE_ELEMENTS.EXPRESS_CHECKOUT).unmount()
      this.elements
        .getElement(STRIPE_ELEMENTS.EXPRESS_CHECKOUT)
        .mount(this.$refs[STRIPE_ELEMENTS.EXPRESS_CHECKOUT])
    },

    initExpressElement() {
      this.mountElement(STRIPE_ELEMENTS.EXPRESS_CHECKOUT, () => {
        const expressElement = this.elements.create("expressCheckout", {
          layout: {
            overflow: "auto",
            maxRows: 2,
            maxColumns: 2
          },
          paymentMethodOrder: [
            "applePay",
            "googlePay",
            "paypal",
            "link",
            "amazonPay"
          ],
          paymentMethods: {
            applePay: "always",
            googlePay: "always"
          }
        })

        expressElement.on("click", event => {
          const options = {
            emailRequired: true,
            billingAddressRequired: this.hasEnabledTaxCollection
          }
          event.resolve(options)
        })

        expressElement.on("ready", data => {
          this.expressHasPaymentMethods = isPresent(
            data.availablePaymentMethods
          )
        })

        expressElement.on("confirm", data => {
          this.isExpressConfirm = true

          const email = this.currentUser?.email || data.billingDetails.email
          if (!this.isAuthenticated) {
            this.setEmail(email)
          }

          if (this.hasEnabledTaxCollection) {
            this.userDetails = {
              name: this.currentUser?.name || data.billingDetails.name || "",
              address: data.billingDetails.address
            }
          }

          this.$nextTick(async () => {
            // ensure email is set
            try {
              await this.getIntent()

              await this.elements.submit()
              await this.confirm()
            } catch (e) {
              console.error(e)
            }
          })
        })

        return expressElement
      })
    },

    setupForm() {
      try {
        if (this.intentWillBeGiven && !this.isIntentGiven) {
          return
        }

        const variables = {}
        const borderRadius = this.borderRadius()
        if (borderRadius) {
          variables.borderRadius = borderRadius
        }

        const appearance = {
          theme: "stripe",
          variables
        }

        const locale = this.currentLocale ? this.currentLocale : "auto"

        const elementOptions = this.isIntentGiven
          ? {
              clientSecret: this.I_intentClientSecret,
              appearance,
              locale
            }
          : {
              mode: this.elementsMode,
              amount: this.amountCents,
              currency: this.currency.toLowerCase(),
              appearance,
              locale
            }

        this.elements =
          this.elements || this.useStripe_stripe?.elements(elementOptions)

        if (!this.elements) {
          if (this.setupAttempt > 10) {
            console.error("Failed to mount Stripe Elements.")
            this.trackError("unknown_error", "Stripe elements not available.")
          } else {
            this.setupAttempt += 1
            setTimeout(this.setupForm, 500)
          }

          return
        }

        if (!this.hideExpressButtons) {
          this.initExpressElement()
        }

        this.initPaymentElement()

        if (!this.isAuthenticated) {
          this.initAuthElement()
        }

        if (this.showAddressElement) {
          this.initAddressElement()
        }
      } catch (e) {
        console.error(e)
      }
    },

    async getIntent() {
      this.isGuestCheckout = false

      if (!this.isAuthenticated) {
        const params = { email: this.email }
        if (isPresent(this.userDetails)) {
          params.name = this.userDetails.name
        }

        try {
          await this.verifySubscriber(params)

          this.isGuestCheckout = true
        } catch (e) {
          if (e.reason === ERROR_REASONS.existing_subscriber) {
            this.$notify(e.message)

            this.showAuthModal()

            if (
              this.isExpressConfirm &&
              this.elements &&
              this.elements.getElement(STRIPE_ELEMENTS.EXPRESS_CHECKOUT)
            ) {
              this.hideExpressWindow()
            }

            return
          } else {
            throw e
          }
        }
      }

      try {
        const userDetails = isPresent(this.userDetails)
          ? {
              name: this.userDetails.name,
              address: this.userDetails.address
            }
          : {}

        const { clientSecret, intentType } = await this.fetchIntent({
          productId: this.productId,
          versionId: this.productVersionId,
          coupon: this.couponCode,
          referral: getReferralData(),
          orderBumps: this.orderBumps || [],
          finalAmount: this.amountCents,
          ...userDetails
        })

        this.I_intentClientSecret = clientSecret
        this.I_intentType = intentType
      } catch (e) {
        if (e?.response?.data?.reason === ERROR_REASONS.existing_subscription) {
          if (this.isAuthenticated) {
            EventBus.$emit(EVENTS.EXISTING_PRODUCT_SUBSCRIPTION, {
              productId: this.productId
            })

            this.$notify(e.response.data.message)

            this.$router.removeHash()
          } else {
            this.$router.push(AUTH_STAGES.INITIAL)
          }

          if (
            this.isExpressConfirm &&
            this.elements &&
            this.elements.getElement(STRIPE_ELEMENTS.EXPRESS_CHECKOUT)
          ) {
            this.hideExpressWindow()
          }
        } else if (
          e?.response?.data?.reason === ERROR_REASONS.order_preview_mismatch ||
          e?.response?.data?.reason === ERROR_REASONS.invalid_version
        ) {
          this.$notify(e.response.data.message)

          this.$emit("refresh")
        } else if (e.response) {
          this.showMessage(e.response.data.message)
        }

        // TODO: not authenticated in backend -> try again with email
        throw e
      }
    },

    async handleSubmit(e) {
      this.isExpressConfirm = false

      e.preventDefault()
      this.hideMessage()

      this.isLoading = true

      if (this.isIntentGiven) {
        await this.elements.submit()
        await this.confirm()
      } else {
        try {
          // https://stripe.com/docs/payments/accept-a-payment-deferred

          const { error: submitError } = await this.elements.submit()

          if (submitError) {
            this.handleError(submitError)
            return
          }

          await this.getIntent()
          await this.confirm()
        } catch (e) {
          console.error(e)
        } finally {
          this.isLoading = false
        }
      }
    },

    async confirm() {
      if (!this.I_intentType) {
        this.trackError("unknown_error", "No intent type given.")
        return
      }

      const callbackParams = {
        [CALLBACK_PARAMS.PRODUCT_ID]: this.productId,
        [CALLBACK_PARAMS.AUTH_TOKEN]: this.authToken,
        [CALLBACK_PARAMS.IS_EXPRESS]: this.isExpressConfirm,
        [CALLBACK_PARAMS.IS_GUEST]: this.isGuestCheckout
      }

      const returnUrl = generateReturnPath(
        window.location.origin + window.location.pathname,
        callbackParams
      )

      const opts = {
        elements: this.elements,
        redirect: "if_required",
        confirmParams: {
          return_url: returnUrl
        }
      }

      if (this.I_intentClientSecret) {
        opts.clientSecret = this.I_intentClientSecret
      }

      const { error, setupIntent, paymentIntent } =
        this.I_intentType === INTENT_TYPES.setup_intent
          ? await this.useStripe_stripe.confirmSetup(opts)
          : await this.useStripe_stripe.confirmPayment(opts)

      if (error) {
        this.handleError(error)
      } else if (setupIntent || paymentIntent) {
        this.$router.push(
          generateReturnPath("", {
            ...callbackParams,
            [CALLBACK_PARAMS.PAYMENT_INTENT_SECRET]:
              paymentIntent?.client_secret,
            [CALLBACK_PARAMS.SETUP_INTENT_SECRET]: setupIntent?.client_secret
          })
        )
      }
    },

    handleError(error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment
      if (
        error.type === "card_error" ||
        error.type === "validation_error" ||
        error.type === "invalid_request_error"
      ) {
        this.showMessage(error.message)
        this.trackError(error.type, error.message)
      } else {
        // Report to bugsnag
        this.showMessage(this.$t("components.checkout.paymentElement.pay"))
        this.trackError("unknown_error", "An unexpected error occurred.")
      }

      this.isLoading = false
    },

    showMessage(message) {
      this.error = message
    },

    hideMessage() {
      this.error = null
    },

    handlePaymentElementChange(event) {
      this.isValid = event.complete
    },

    handleUserDetailsChange() {
      if (this.isEmailValid(this.email) && isPresent(this.userDetails)) {
        return this.$emit("change", this.userDetails)
      }
    },

    handleAddressElementChange(event) {
      this.isAddressValid = event.complete

      if (
        event.complete &&
        JSON.stringify(event.value) !== JSON.stringify(this.userDetails)
      ) {
        this.userDetails = event.value
        debounce(this.handleUserDetailsChange, 600)
      }
    },

    trackError(reason, message) {
      this.trackActivity({
        activityType: ACTIVITY_TYPES.CHECKOUT_ERROR,
        data: {
          reason,
          message,
          product: { id: this.productId }
        }
      })
    },

    reset() {
      this.error = null
      this.elements = null
      this.authToken = null

      this.initializedElements = {}
      this.loadingElements = {}
    }
  },

  beforeDestroy() {
    Object.keys(this.initializedElements).forEach(elementName => {
      this.elements?.getElement(elementName)?.destroy()
    })

    this.reset()
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.payment-element__errors {
  margin: $size--4 0;
  display: flex;
  border: 1px solid $color-ui--red;
  box-shadow: $box-shadow--small;
  padding: $size--4;
  border-radius: 5px;
  color: $color-ui--grey-90;
  font-size: $font-size--base;
  animation: shake-x 0.6s ease;

  .v-icon {
    color: $color-ui--red;
    font-size: $font-size--large;
    margin-right: $size--2;
  }
}

.payment-element__wrapper {
  padding: $size--4;
  padding-top: $size--6;
  border: 1px solid $color-ui--grey-20;
  border-radius: var(--border-radius);
  position: relative;

  &.--with-express {
    padding-top: $size--8;

    &:before {
      content: "or";
      background: white;
      position: absolute;
      text-align: center;
      top: -20px;
      left: 50%;
      width: 100px;
      margin-left: -60px;
      padding: 8px;
      color: $color-ui--grey-60;
    }
  }

  &.--guest {
    padding-top: $size--5;
  }
}

.payment-element__placeholder-element {
  border: 1px solid $color-ui--grey-30;
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02);
  border-radius: 5px;
  padding: $size--6;
  flex-grow: 1;
  margin: 0 $size--1;
  animation: load-pulse 1.2s infinite ease-in-out;
  position: relative;

  &:after {
    content: "";
    position: absolute;
    left: $size--3;
    top: $size--3;
    height: $size--5;
    width: 33%;
    background: $color-ui--grey-15;
  }
}

.payment-element__cta-row {
  opacity: 0;
  animation: fade-in 0.4s ease forwards;
  animation-delay: 0.8s;
}

.payment-element__express-placeholder {
  margin: 4px 0 !important;
  border-radius: var(--input-border-radius) !important;
}
</style>
